import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { useNewbornIPPBirthingInformation } from "@/encounters/components/NewbornIPPEncounter/hooks/useNewbornIPPBirthingInformation";
import { NewbornIPPBirthDetailsQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const BirthDetailsDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const birthingInformation = useNewbornIPPBirthingInformation();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornIPPBirthDetails,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousBirthDetailsAnswers = getQuestionnaireAnswers(previousVersion);
  const currentBirthDetailsAnswers = getQuestionnaireAnswers(currentVersion);

  const previousSex =
    previousBirthDetailsAnswers[NewbornIPPBirthDetailsQuestionsLinkIds.Sex]?.valueString;
  const currentSex =
    currentBirthDetailsAnswers[NewbornIPPBirthDetailsQuestionsLinkIds.Sex]?.valueString;
  const previousSexSpecify =
    previousBirthDetailsAnswers[NewbornIPPBirthDetailsQuestionsLinkIds.SexSpecify]?.valueString;
  const currentSexSpecify =
    currentBirthDetailsAnswers[NewbornIPPBirthDetailsQuestionsLinkIds.SexSpecify]?.valueString;
  const previousBabyName =
    previousBirthDetailsAnswers[NewbornIPPBirthDetailsQuestionsLinkIds.BabyName]?.valueString;
  const currentBabyName =
    currentBirthDetailsAnswers[NewbornIPPBirthDetailsQuestionsLinkIds.BabyName]?.valueString;

  const timeSinceBirth = birthingInformation.timeSinceBirth
    ? `${birthingInformation.timeSinceBirth} days`
    : "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Birth Details</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Time</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={birthingInformation.birthTime}
                  previous={birthingInformation.birthTime}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time since Birth</TextWrapper>
                <WordsDiff showRemoved current={timeSinceBirth} previous={timeSinceBirth} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Sex</TextWrapper>
                <WordsDiff showRemoved current={currentSex} previous={previousSex} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Specify</TextWrapper>
                <WordsDiff showRemoved current={currentSexSpecify} previous={previousSexSpecify} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Baby's Name</TextWrapper>
                <WordsDiff showRemoved current={currentBabyName} previous={previousBabyName} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Time</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={birthingInformation.birthTime}
                  previous={birthingInformation.birthTime}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Time since Birth</TextWrapper>
                <WordsDiff showRemoved current={timeSinceBirth} previous={timeSinceBirth} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Sex</TextWrapper>
                <WordsDiff current={currentSex} previous={previousSex} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Specify</TextWrapper>
                <WordsDiff current={currentSexSpecify} previous={previousSexSpecify} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Baby's Name</TextWrapper>
                <WordsDiff current={currentBabyName} previous={previousBabyName} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default BirthDetailsDiff;
